// Main API service - provides all services in one place for easy importing
import {
  httpClient,
  registerLogoutCallback,
  registerSnackbarCallback,
  resetLogoutFlag,
  setupResponseInterceptors,
} from './http';

// Activity service exports
export {
  ACTIVITY_TYPES,
  getActivityData,
  logAIServiceUsed,
  logDocumentUploaded,
  logLogin,
  logLogout,
  logSecurityEvent,
  logTenderCreated,
  logUserActivity,
} from './activityService';

import { ACTIVITY_TYPES, logUserActivity } from './activityService';

// Admin service exports
export {
  getPlatformSettings,
  getUsersForClient,
  updatePlatformSettings,
} from './adminService';

// Auth service exports
export {
  createUser,
  getAccountDetails,
  logoutUser,
  validateInvitationToken,
} from './authService';
// Context Hunter exports
export {
  huntContext,
  indexStorage,
  listFolders,
  moveKnowledgeDocument,
  resetStorageIndex,
  resolveExternalSite,
  scanStorage,
  searchContextFiles,
} from './contextHunterService';

import type { CreateTicketData, TicketFilters } from './helpService';

// Help service exports
export {
  createHelpTicket,
  createTicketResponse,
  getHelpTicketById,
  getHelpTickets,
  updateHelpTicket,
} from './helpService';
// Library service exports
export {
  deleteLibraryFile,
  fetchLibraryFileList,
  saveLibraryFileMetadata,
  subscribeToLibraryFiles,
  uploadLibraryFile,
} from './libraryService';
export {
  getStats,
  migrateSmartRag,
  reportError,
  submitFeedback,
} from './platformService';
export { shareTender, unshareTender } from './sharingService';
export type { CreateTicketData, TicketFilters };

import type { StrategicInsightsData, StrategicInsightsFilters } from './tenderAIService';

export {
  compileDraft,
  generateAdHocDocument,
  generateCVs,
  generateVisualExport,
  getStrategicInsights,
  regenerateDocument,
  searchKnowledgeBase,
  sectionChat,
  tenderInquiry,
} from './tenderAIService';
export {
  addTenderToDashboard,
  archiveStoreTender,
  archiveTender,
  createTender,
  deleteTender,
  getTenders,
  pursueTender,
  updateTenderAdminOnly,
} from './tenderCoreService';
export {
  deleteDraftForTenderReview,
  deleteSupportDocForTender,
  deleteTenderForm,
  fetchDraftsForTenderReview,
  fetchSupportDocsForTender,
  getTenderFileDownloadUrl,
  updateDocumentCategory,
  uploadAssociatedTenderFile,
  uploadDraftForTenderReview,
  uploadSupportDocForTender,
  uploadTenderCV,
  uploadTenderDraft,
  uploadTenderForm,
} from './tenderFileService';
// Tender service exports
export {
  fetchTenderSections,
  saveTenderSection,
} from './tenderSectionService';
export type { StrategicInsightsData, StrategicInsightsFilters };

import type { TenderType } from './tenderService';

export type { TenderType };

import type { AccountDetailsUpdate, UpdateUserData, UserFilters } from './userService';

// User service exports
export {
  allocateUserLimits,
  deleteUser,
  getUsers,
  inviteUser,
  resetPassword,
  revokeInvitation,
  updateAccountDetails,
  updateInvitation,
  updateUser,
  updateUserRole,
} from './userService';
export type { AccountDetailsUpdate, UpdateUserData, UserFilters };
// HTTP client exports for backward compatibility
export { registerLogoutCallback, registerSnackbarCallback, resetLogoutFlag };

// Export the httpClient instance as apiClient for backward compatibility
export const apiClient = httpClient.getInstance();

// Setup response interceptors on the HTTP client
const client = httpClient.getInstance();
setupResponseInterceptors(client, { logUserActivity, ACTIVITY_TYPES });

import type { UserData } from './authService';

export const apiService = {
  // Auth functions
  createUser: async (userData: UserData) => {
    const { createUser } = await import('./authService');
    return createUser(userData);
  },
  validateInvitationToken: async (token: string) => {
    const { validateInvitationToken } = await import('./authService');
    return validateInvitationToken(token);
  },
  getAccountDetails: async () => {
    const { getAccountDetails } = await import('./authService');
    return getAccountDetails();
  },

  updateAccountDetails: async (details: AccountDetailsUpdate) => {
    const { updateAccountDetails } = await import('./userService');
    return updateAccountDetails(details);
  },

  clearUnverifiedPhoneNumber: async () => {
    const { clearUnverifiedPhoneNumber } = await import('./authService');
    return clearUnverifiedPhoneNumber();
  },

  // Tender functions
  getStrategicInsights: async (filters: StrategicInsightsFilters) => {
    const { getStrategicInsights } = await import('./tenderAIService');
    return getStrategicInsights(filters);
  },
  getTenders: async (options?: { includeArchived?: boolean }) => {
    const { getTenders } = await import('./tenderCoreService');
    return getTenders(options);
  },

  createTender: async (
    files: File | File[],
    tenderType: TenderType = 'uploaded',
    metadata?: { title?: string; tenderIssuer?: string; isGrant?: boolean } // INTERIM WHITE-LABELING: isGrant
  ) => {
    const { createTender } = await import('./tenderCoreService');
    return createTender(files, tenderType, metadata);
  },

  uploadTenderDraft: async (tenderId: string, draftFile: File) => {
    const { uploadTenderDraft } = await import('./tenderFileService');
    return uploadTenderDraft(tenderId, draftFile);
  },

  deleteTender: async (tenderId: string, deleteReason: string) => {
    const { deleteTender } = await import('./tenderCoreService');
    return deleteTender(tenderId, deleteReason);
  },

  getStats: async () => {
    const { getStats } = await import('./platformService');
    return getStats();
  },

  // User functions
  getUsers: async (filters: UserFilters = {}) => {
    const { getUsers } = await import('./userService');
    return getUsers(filters);
  },

  updateUser: async (updateData: UpdateUserData) => {
    const { updateUser } = await import('./userService');
    return updateUser(updateData);
  },

  // Admin functions

  // Help functions
  createHelpTicket: async (ticketData: CreateTicketData) => {
    const { createHelpTicket } = await import('./helpService');
    return createHelpTicket(ticketData);
  },

  getHelpTickets: async (filters: TicketFilters = {}) => {
    const { getHelpTickets } = await import('./helpService');
    return getHelpTickets(filters);
  },

  // Public Tenders functions
  addTenderToDashboard: async (storeTenderId: string) => {
    const { addTenderToDashboard } = await import('./tenderCoreService');
    return addTenderToDashboard(storeTenderId);
  },

  pursueTender: async (tenderId: string) => {
    const { pursueTender } = await import('./tenderCoreService');
    return pursueTender(tenderId);
  },

  archiveTender: async (tenderId: string, reason: string) => {
    const { archiveTender } = await import('./tenderCoreService');
    return archiveTender(tenderId, reason);
  },

  archiveStoreTender: async (storeTenderId: string, reason: string, title: string) => {
    const { archiveStoreTender } = await import('./tenderCoreService');
    return archiveStoreTender(storeTenderId, reason, title);
  },

  getTenderFileDownloadUrl: async (gsUri: string) => {
    const { getTenderFileDownloadUrl } = await import('./tenderFileService');
    return getTenderFileDownloadUrl(gsUri);
  },

  uploadAssociatedTenderFile: async (params: {
    tenderId: string;
    file: File;
    uploadCategory: string;
  }) => {
    const { uploadAssociatedTenderFile } = await import('./tenderFileService');
    return uploadAssociatedTenderFile(params);
  },
  deleteSupportDocForTender: async (params: {
    userId: string;
    clientId: string;
    tenderId: string;
    fileName: string;
    docCategory?: string;
    docId?: string;
  }) => {
    const { deleteSupportDocForTender } = await import('./tenderFileService');
    return deleteSupportDocForTender(params);
  },

  regenerateDocument: async (params: {
    clientId: string;
    userId: string;
    tenderId: string;
    userSpecificBucket: string;
    docType: string;
    sourceDraftStorageUri?: string;
    sourceDraftGcsPath?: string; // Deprecated
    sourceDraftFileName?: string;
  }) => {
    const { regenerateDocument } = await import('./tenderAIService');
    return regenerateDocument(params);
  },

  generateCVs: async (params: {
    userId: string;
    tenderId: string;
    clientId: string;
    cv_files?: string[];
    jobId?: string;
  }) => {
    const { generateCVs } = await import('./tenderAIService');
    return generateCVs(params);
  },

  uploadTenderCV: async (params: {
    userId: string;
    tenderId: string;
    clientId: string;
    file: File;
    actualBucketName: string;
    docType?: string;
  }) => {
    const { uploadTenderCV } = await import('./tenderFileService');
    return uploadTenderCV(params);
  },

  reportError: async (params: { tenderId: string; error: string; component?: string }) => {
    const { reportError } = await import('./platformService');
    return reportError(params);
  },

  updateTenderAdminOnly: async (tenderId: string, isAdmin: boolean) => {
    const { updateTenderAdminOnly } = await import('./tenderCoreService');
    return updateTenderAdminOnly(tenderId, isAdmin);
  },

  migrateSmartRag: async (params: { client_id: string }) => {
    const { migrateSmartRag } = await import('./platformService');
    return migrateSmartRag(params);
  },

  indexStorage: async (params: {
    userId: string;
    clientId: string;
    folderPaths: string[];
    siteId?: string;
    driveId?: string;
    mode?: string;
    collectionType?: string;
  }) => {
    const { indexStorage } = await import('./contextHunterService');
    return indexStorage(params);
  },
};
